import { useMemo } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  GroupEntity,
  parseEntityRef,
  RELATION_HAS_MEMBER,
  UserEntity,
} from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
  /*
   * Grid of member chips. Each member is its own cell so an overflow
   * wraps cleanly to the next row; we deliberately don't use a horizontal
   * list because long names on a narrow column (1/3 width) would
   * truncate awkwardly.
   */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: theme.spacing(1.25),
  },
  member: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0.75),
    borderRadius: 6,
    backgroundColor: boschGray[95],
    border: `1px solid ${boschGray[90]}`,
    minWidth: 0,
    textDecoration: 'none',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: boschBlue[80],
    },
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '0.75rem',
    backgroundColor: boschBlue[50],
    color: '#FFFFFF',
    fontWeight: 700,
    flexShrink: 0,
  },
  info: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.1,
  },
  name: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: boschGray[15],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  role: {
    fontSize: '0.7rem',
    color: boschGray[40],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export type TeamMembersProps = {
  /** `group:<namespace>/<name>` ref (or just `<name>` — we'll default
   *  the kind/namespace). The catalog API resolves it to a GroupEntity. */
  groupRef: string;
  title?: string;
  /** Render each member's `spec.profile.email` underneath the name.
   *  Off by default — corporate emails are noisy and add little
   *  value when the group already has 30 members. */
  showEmail?: boolean;
};

/** Initials helper, used for the fallback avatar text when a user
 *  entity has no `spec.profile.picture`. Strips accents and grabs
 *  the first letter of each of the first two whitespace-separated
 *  tokens, max 2 chars. */
const initialsOf = (name: string): string => {
  const cleaned = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const parts = cleaned.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
};

const displayNameOf = (entity: UserEntity): string => {
  const profileName = entity.spec?.profile?.displayName;
  if (profileName) return profileName;
  return entity.metadata.title ?? entity.metadata.name;
};

/**
 * Live list of members in a `Group` entity. Lookup path:
 *
 *   1. Fetch the Group entity by ref so we can show its display name
 *      in the card title (when the admin doesn't override it).
 *   2. Pull every relation of kind `hasMember` from the group — those
 *      are User entity refs.
 *   3. Batch-fetch the User entities to render avatars / display names.
 *
 * Why fetch the group AND the users separately:
 *   - The catalog API doesn't expose a "give me all child entities"
 *     endpoint, so the relations array on the Group is the source of
 *     truth.
 *   - Batching the user fetch (`getEntitiesByRefs`) keeps this to
 *     two network round-trips no matter how many members the team
 *     has — a 200-member group otherwise generates 200 requests.
 */
export const TeamMembers = ({
  groupRef,
  title,
  showEmail = false,
}: TeamMembersProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  // Normalise the ref ONCE so the cache key is stable; otherwise React
  // would compare a new string each render (`group:default/foo`
  // vs `foo`) even though they resolve to the same entity.
  const normalisedRef = useMemo(() => {
    if (!groupRef) return undefined;
    try {
      const parsed = parseEntityRef(groupRef, {
        defaultKind: 'group',
        defaultNamespace: 'default',
      });
      return `${parsed.kind}:${parsed.namespace}/${parsed.name}`;
    } catch {
      return undefined;
    }
  }, [groupRef]);

  const {
    value,
    loading,
    error,
  } = useAsync(async () => {
    if (!normalisedRef) return undefined;
    const group = (await catalogApi.getEntityByRef(normalisedRef)) as
      | GroupEntity
      | undefined;
    if (!group) return undefined;
    const memberRefs = (group.relations ?? [])
      .filter(r => r.type === RELATION_HAS_MEMBER)
      .map(r => r.targetRef);
    if (memberRefs.length === 0) {
      return { group, members: [] as UserEntity[] };
    }
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: memberRefs,
    });
    const members = items.filter(
      (e): e is UserEntity => !!e && e.kind === 'User',
    );
    return { group, members };
  }, [catalogApi, normalisedRef]);

  const groupTitle =
    title ??
    value?.group.metadata.title ??
    value?.group.metadata.name ??
    groupRef ??
    'Team members';

  return (
    <InfoCard title={groupTitle}>
      {!normalisedRef && (
        <Typography className={classes.empty}>
          Provide a group reference (e.g. <code>group:default/devops</code>)
          via the widget config.
        </Typography>
      )}
      {loading && (
        <Box className={classes.loading}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Typography className={classes.empty}>
          Failed to load group: {error.message}
        </Typography>
      )}
      {!loading && !error && value && value.members.length === 0 && (
        <Typography className={classes.empty}>
          This group has no members yet.
        </Typography>
      )}
      {!loading && !error && value && value.members.length > 0 && (
        <div className={classes.grid}>
          {value.members.map(user => (
            <MemberChip
              key={user.metadata.name}
              user={user}
              showEmail={showEmail}
              classes={classes}
            />
          ))}
        </div>
      )}
    </InfoCard>
  );
};

/* Split out so the parent component stays a flat list of branches.
 * Memoising at the chip level isn't worth it — the parent is the
 * useAsync owner and React will skip re-renders for unchanged refs. */
const MemberChip = ({
  user,
  showEmail,
  classes,
}: {
  user: UserEntity;
  showEmail: boolean;
  classes: ReturnType<typeof useStyles>;
}) => {
  const name = displayNameOf(user);
  const picture =
    typeof user.spec?.profile?.picture === 'string'
      ? user.spec.profile.picture
      : undefined;
  const email = user.spec?.profile?.email;
  const href = `/catalog/${
    user.metadata.namespace ?? 'default'
  }/user/${user.metadata.name}`;
  return (
    <Link to={href} className={classes.member} aria-label={`Open ${name}`}>
      <Avatar src={picture} className={classes.avatar} alt="">
        {picture ? undefined : initialsOf(name)}
      </Avatar>
      <span className={classes.info}>
        <span className={classes.name}>{name}</span>
        {showEmail && email && (
          <span className={classes.role}>{email}</span>
        )}
      </span>
    </Link>
  );
};

